import { useState, useEffect } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, Plus, Pencil } from 'lucide-react'
import { createVendorApi, updateVendorApi } from '@frontend/lib/api'
import { TagsInput } from '@frontend/components/shared/TagsInput'
import type { CreateVendorRequest, UpdateVendorRequest, VendorDetail } from '@common/types'
import { VENDOR_BUSINESS_UNITS, VENDOR_RISK_LEVELS } from '@common/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

type VendorFormData = CreateVendorRequest

interface AddVendorModalProps {
  mode: 'create'
  open: boolean
  onOpenChange: (open: boolean) => void
  vendor?: never
}

interface EditVendorModalProps {
  mode: 'edit'
  open: boolean
  onOpenChange: (open: boolean) => void
  vendor: VendorDetail
}

type VendorModalProps = AddVendorModalProps | EditVendorModalProps

const emptyFormData: VendorFormData = {
  name: '',
  aliases: [],
  website: null,
  trustCenterUrl: null,
  privacyPolicyUrl: null,
  tosUrl: null,
  businessUnit: null,
  risk: null,
  storesPii: null,
  isSubprocessor: null,
}

function vendorToFormData(vendor: VendorDetail): VendorFormData {
  return {
    name: vendor.name,
    aliases: vendor.aliases,
    website: vendor.website,
    trustCenterUrl: vendor.trustCenterUrl,
    privacyPolicyUrl: vendor.privacyPolicyUrl,
    tosUrl: vendor.tosUrl,
    businessUnit: vendor.businessUnit,
    risk: vendor.risk,
    storesPii: vendor.storesPii,
    isSubprocessor: vendor.isSubprocessor,
  }
}

export function VendorModal(props: VendorModalProps) {
  const { mode, open, onOpenChange } = props
  const vendor = mode === 'edit' ? props.vendor : undefined
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<VendorFormData>({ ...emptyFormData })
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setFormData(vendor ? vendorToFormData(vendor) : { ...emptyFormData })
      setError(null)
    }
  }, [open, vendor])

  const createMutation = useMutation({
    mutationFn: (data: CreateVendorRequest) => createVendorApi(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      onOpenChange(false)
      toast.success(`Vendor "${data.vendor.name}" created`)
    },
    onError: (err) => {
      handleMutationError(err, 'Failed to create vendor')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: UpdateVendorRequest) => updateVendorApi(vendor!.id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      queryClient.invalidateQueries({ queryKey: ['vendor', vendor!.id] })
      onOpenChange(false)
      toast.success(`Vendor "${data.vendor.name}" updated`)
    },
    onError: (err) => {
      handleMutationError(err, 'Failed to update vendor')
    },
  })

  function handleMutationError(err: Error, fallback: string) {
    if (isHTTPError(err)) {
      const data = err.data as { error?: string }
      setError(data.error || fallback)
    } else {
      setError(fallback)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    const cleaned = {
      ...formData,
      name: formData.name.trim(),
      website: formData.website || null,
      trustCenterUrl: formData.trustCenterUrl || null,
      privacyPolicyUrl: formData.privacyPolicyUrl || null,
      tosUrl: formData.tosUrl || null,
    }

    if (mode === 'create') {
      createMutation.mutate(cleaned)
    } else {
      updateMutation.mutate(cleaned)
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const isCreate = mode === 'create'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isCreate ? <Plus className="h-5 w-5" /> : <Pencil className="h-5 w-5" />}
            {isCreate ? 'Add Vendor' : 'Edit Vendor'}
          </DialogTitle>
          <DialogDescription>
            {isCreate ? 'Add a new vendor to track compliance for.' : 'Update vendor details.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="vendor-name" className="block text-sm font-medium mb-1">
              Vendor Name <span className="text-red-500">*</span>
            </label>
            <input
              id="vendor-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="e.g., Acme Corp"
              disabled={isSubmitting}
              autoComplete="off"
              data-1p-ignore
            />
          </div>

          <TagsInput
            label="Aliases"
            values={formData.aliases ?? []}
            onChange={(aliases) => setFormData((prev) => ({ ...prev, aliases }))}
            placeholder="Type alias and press Enter or comma..."
            helpText="Alternative names for Drata matching (e.g. AWS for Amazon Web Services)"
            disabled={isSubmitting}
          />

          <div>
            <label htmlFor="vendor-website" className="block text-sm font-medium mb-1">
              Website
            </label>
            <input
              id="vendor-website"
              type="url"
              value={formData.website ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, website: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://..."
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="vendor-trust-center" className="block text-sm font-medium mb-1">
              Trust Center URL
            </label>
            <input
              id="vendor-trust-center"
              type="url"
              value={formData.trustCenterUrl ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, trustCenterUrl: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://..."
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="vendor-privacy" className="block text-sm font-medium mb-1">
              Privacy Policy URL
            </label>
            <input
              id="vendor-privacy"
              type="url"
              value={formData.privacyPolicyUrl ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, privacyPolicyUrl: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://..."
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="vendor-tos" className="block text-sm font-medium mb-1">
              Terms of Service URL
            </label>
            <input
              id="vendor-tos"
              type="url"
              value={formData.tosUrl ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, tosUrl: e.target.value }))}
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="https://..."
              disabled={isSubmitting}
            />
          </div>

          <div>
            <label htmlFor="vendor-bu" className="block text-sm font-medium mb-1">
              Business Unit
            </label>
            <select
              id="vendor-bu"
              value={formData.businessUnit ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  businessUnit: (e.target.value || null) as CreateVendorRequest['businessUnit'],
                }))
              }
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            >
              <option value="">Select...</option>
              {VENDOR_BUSINESS_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="vendor-risk" className="block text-sm font-medium mb-1">
              Risk
            </label>
            <select
              id="vendor-risk"
              value={formData.risk ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  risk: (e.target.value || null) as CreateVendorRequest['risk'],
                }))
              }
              className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={isSubmitting}
            >
              <option value="">Select...</option>
              {VENDOR_RISK_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={formData.storesPii ?? false}
              onChange={(e) => setFormData((prev) => ({ ...prev, storesPii: e.target.checked }))}
              className="rounded border-border"
              disabled={isSubmitting}
            />
            Stores PII
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={formData.isSubprocessor ?? false}
              onChange={(e) => setFormData((prev) => ({ ...prev, isSubprocessor: e.target.checked }))}
              className="rounded border-border"
              disabled={isSubmitting}
            />
            Is Subprocessor
          </label>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  {isCreate ? 'Creating...' : 'Saving...'}
                </>
              ) : isCreate ? (
                'Add Vendor'
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
